import { CheckCircle, Email, Error } from '@mui/icons-material';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';

import { useEffect, useRef, useState } from 'react';
import IconStatusButton from '../../common-components/icon-status-button/icon-status-button';
import useUserRoleAndShortId from '../../hooks/user-details/useUserDetails';
import { Port } from '../../maritime-menu-options/world-ports-panel/world-ports.model';
import { ProReportResponse, ProVesselTypes } from '../../models/pro.model';
import {
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../utils/analytics';
import DownloadHelpers from '../../utils/download.utils';
import { buildProReportFileName } from '../../utils/filename.utils';
import ResponseDialog from '../pro-report-dialog/response-dialog';
import createEml from './create-eml';
import getProReportEmailContent from './pro-report-email-utils';

function ProReportEmailDownloadButton({
  port,
  report,
  selectedVesselType,
  fetching,
}: {
  port: Port;
  report: ProReportResponse;
  selectedVesselType: ProVesselTypes;
  fetching: boolean;
}) {
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const userDetails = useUserRoleAndShortId();
  const lastLoggedEvent = useRef<string | null>(null);

  const tooltipContent = () => (
    <Box>
      <Typography variant="body2">
        This will create and download a PRO report email in .eml format for easy
        sharing and archiving.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {`The file will be saved to your device's default download location and
        can be opened (and sent onwards) in most email clients.`}
      </Typography>
    </Box>
  );

  useEffect(() => {
    setDownloadInProgress(false);
    setDownloadComplete(false);
    setDownloadError(false);
  }, [selectedVesselType, port]);

  const downloadButtonText = () => {
    if (downloadInProgress) return 'Creating Email...';
    if (downloadComplete) return 'Downloaded Email';
    if (downloadError) return 'Error';
    return 'Create Email';
  };

  const getDownloadIcon = () => {
    if (downloadInProgress) return <CircularProgress size={12} />;
    if (downloadComplete) return <CheckCircle />;
    if (downloadError) return <Error />;
    return <Email />;
  };

  const handleDownload = async () => {
    setDownloadInProgress(true);
    setDownloadComplete(false);
    setDownloadError(false);
    try {
      const { subject, htmlBody, textBody } = await getProReportEmailContent(
        port,
        report,
        selectedVesselType
      );

      const eml = createEml({
        subject,
        html: htmlBody ?? '',
        text: textBody ?? '',
      });

      const emlFileName = buildProReportFileName(
        port.NAME,
        port.COUNTRY,
        port.UNLOCODE,
        selectedVesselType,
        'eml'
      );

      await DownloadHelpers.generateAndDownloadEML(emlFileName, eml).then(
        () => {
          setDownloadComplete(true);
          // Build a unique key for this event to prevent logging multiple analytics events
          // for the same report download. This ensures that if the user downloads the same
          // report instance multiple times, only the first download is logged.
          const eventKey = `${port.UNLOCODE}-${selectedVesselType}-${userDetails.userAnonymisedId}`;
          // Only log if this event is new
          if (lastLoggedEvent.current !== eventKey) {
            logAnalyticsEvent('Ports', 'ProReportEmailDownloaded', [
              getUserAnalyticsEventLabel(userDetails),
            ]);
            lastLoggedEvent.current = eventKey;
          }
        }
      );
    } catch (error) {
      setDownloadError(true);
    } finally {
      setDownloadInProgress(false);
    }
  };

  return (
    <>
      <IconStatusButton
        tooltipContent={tooltipContent()}
        onClick={handleDownload}
        fullWidth
        statusIcon={getDownloadIcon()}
        statusText={downloadButtonText()}
        disabled={fetching || !report || downloadInProgress}
        dataTestId="pro-report-email-download-button"
      />
      <ResponseDialog
        open={downloadError}
        onClose={() => {
          setDownloadComplete(false);
          setDownloadError(false);
        }}
        headerText="PRO Report Email Download Error"
        titleText="An error occurred while downloading the PRO report email for this destination."
        content={
          <>
            <Grid
              container
              spacing={1}
              sx={{ paddingLeft: 2, width: '100%', mb: 1 }}
            >
              <Grid item xs={4}>
                <Typography>Port Name:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{port.NAME || 'Unknown Port'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Port Unlocode:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{port.UNLOCODE || 'Unknown Port Code'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Vessel Type:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {selectedVesselType === ProVesselTypes.NO_VESSEL_TYPE
                    ? 'All Vessels'
                    : selectedVesselType}
                </Typography>
              </Grid>
            </Grid>
            <Typography>
              Please try again later. If the issue persists, contact support.
            </Typography>
          </>
        }
      />
    </>
  );
}

export default ProReportEmailDownloadButton;
